/**
 * Client-side gate for the V2 Route (`/v2`).
 *
 * This is a **short-lived beta flag**: the V2 App Shell is opt-in for a
 * small group of testers on the beta stage while Phase 1 hardens, and
 * the flag is expected to be deleted (along with this file) once V2 is
 * enabled for everyone. Because the audience is bounded and the
 * lifetime is measured in weeks, the flag is stored in the browser's
 * `localStorage` rather than served from a backend endpoint - there is
 * no `/api/config` fetch, no loading state, and no per-stage server
 * plumbing to build and maintain.
 *
 * ## How a tester opts in
 *
 * The one-time opt-in is a URL bookmark: visit
 * `https://<host>/?v2=1` (or `/v2?v2=1`). The `?v2=1` query parameter
 * is intercepted at page load, converted into a localStorage entry,
 * and stripped from the URL so the tester's bookmarks stay clean.
 * From then on, every subsequent visit to `/v2` on the same browser
 * renders the App Shell.
 *
 * Opt-out is the same trick with `?v2=0`.
 *
 * Manual opt-in without a URL is also fine:
 * `localStorage.setItem("dacw:v2Enabled", "true")` in the browser
 * devtools.
 *
 * ## Failure modes
 *
 * `localStorage` can be unavailable (SSR, cookies-blocked, private
 * browsing quotas). Every function here treats "cannot read/write
 * storage" as "flag is disabled" - the safe default is the legacy
 * experience, so users who cannot participate in the beta preview
 * still get the working product.
 */

/**
 * localStorage key that gates the V2 Route. The `dacw:` prefix
 * ("Devices ASIN Central Website") namespaces the entry so it does
 * not collide with keys owned by shared libraries or embedded
 * modules on the same origin.
 */
const V2_ENABLED_KEY = "dacw:v2Enabled";

/** URL query parameter that persists an opt-in / opt-out choice. */
const OPT_IN_QUERY_PARAM = "v2";

/**
 * Values of `?v2=` that flip the localStorage entry on / off.
 * Any other value (including empty) is treated as "no change" so
 * that a stray `?v2=` in a copy-pasted link cannot accidentally
 * revoke a tester's opt-in.
 */
const OPT_IN_VALUES = new Set(["1", "true"]);
const OPT_OUT_VALUES = new Set(["0", "false"]);

/**
 * Reads `?v2=...` from the current URL, persists the resulting
 * localStorage state, and strips the parameter from the address bar
 * without adding a history entry.
 *
 * Idempotent - safe to call multiple times per page load. Silently
 * does nothing when:
 *   - the query parameter is absent,
 *   - the parameter's value is not a recognized on/off string,
 *   - `localStorage` or `history.replaceState` are unavailable.
 *
 * Called from `main.tsx` exactly once, before React mounts, so the
 * subsequent `isV2Enabled()` read in `App.tsx` observes the freshly
 * persisted state.
 */
export function applyV2OptInFromUrl(): void {
  if (typeof window === "undefined") return;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const value = url.searchParams.get(OPT_IN_QUERY_PARAM);
  if (value === null) return;

  const normalized = value.toLowerCase();
  try {
    if (OPT_IN_VALUES.has(normalized)) {
      window.localStorage.setItem(V2_ENABLED_KEY, "true");
    } else if (OPT_OUT_VALUES.has(normalized)) {
      window.localStorage.removeItem(V2_ENABLED_KEY);
    } else {
      return; // unrecognized value: leave storage alone, leave URL alone
    }
  } catch {
    // storage blocked - still strip the URL param so the tester does
    // not see a confusing `?v2=1` linger after clicking the bookmark.
  }

  url.searchParams.delete(OPT_IN_QUERY_PARAM);
  try {
    window.history.replaceState(null, "", url.toString());
  } catch {
    // history API missing (very old browsers): non-fatal.
  }
}

/**
 * Synchronous read of the V2 Route gate.
 *
 * Returns true only when a previous `applyV2OptInFromUrl()` call
 * persisted `"true"` under `dacw:v2Enabled` (or a tester set the key
 * manually in devtools). Any other state - unset, wrong value,
 * storage unavailable - resolves to `false`, matching the "safe
 * default is Legacy" invariant.
 */
export function isV2Enabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(V2_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}
